import {
  CALENDAR_MAX_ITEMS,
  CALENDAR_MAX_RANGE_DAYS,
  type CalendarResponse,
  type ColorToken,
  type TaskDTO,
} from '@hearth/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, PanelLeft, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button, EmptyState, IconButton, SidePanel, Skeleton, Spinner } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { useHotkeys } from '../../hooks/useHotkeys';
import { useLookups } from '../../hooks/useMeta';
import { useNow } from '../../hooks/useNow';
import { api, apiUrl } from '../../lib/api';
import { formatNumber } from '../../lib/format';
import { TaskFormPanel, type TaskFormDefaults } from '../tasks/TaskFormPanel';
import { taskColor } from '../tasks/taskAppearance';
import { AgendaView } from './AgendaView';
import { CalendarSidebar, type PersonOption } from './CalendarSidebar';
import { EntryDetailsPopover } from './EntryDetailsPopover';
import {
  AGENDA_DAYS,
  CALENDAR_VIEWS,
  type CalendarPreferences,
  type CalendarView,
  type DraftSlot,
  assignPersonColors,
  calendarQueryParams,
  daySlot,
  formatDateParam,
  loadPreferences,
  nextHalfHour,
  parseDateParam,
  parseView,
  periodTitle,
  savePreferences,
  slotAt,
  startOfDay,
  stepDate,
  toCalendarEntries,
  visibleRange,
  wallMinutes,
} from './model';
import { MonthView } from './MonthView';
import { QuickCreatePopover } from './QuickCreatePopover';
import { TimeGridView } from './TimeGridView';
import { CALENDAR_RANGE_KEY, type NewEntry, useCalendarActions } from './useCalendarActions';

const VIEW_LABELS: Record<CalendarView, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  agenda: 'Agenda',
};
const VIEW_HOTKEYS: Record<CalendarView, string> = { day: 'D', week: 'W', month: 'M', agenda: 'A' };
const STEP_NOUNS: Record<CalendarView, string> = {
  day: 'day',
  week: 'week',
  month: 'month',
  agenda: 'period',
};

/** Longest agenda one request may cover; whole days short of the limit so a 25-hour DST day still fits. */
const MAX_AGENDA_DAYS = Math.floor((CALENDAR_MAX_RANGE_DAYS - 1) / AGENDA_DAYS) * AGENDA_DAYS;

type Selection = { task: TaskDTO; anchor: HTMLElement };
type Draft = { slot: DraftSlot; anchor: HTMLElement | null; title: string };
type Editor = { task: TaskDTO } | { defaults: TaskFormDefaults };

/** The team calendar: tasks, follow-ups, meetings and notes on a day, week, month or agenda. */
export function CalendarPage() {
  const { user } = useAuth();
  // The authenticated shell only renders pages for a signed-in user.
  if (!user) return null;
  return <Calendar userId={user.id} userName={user.name} />;
}

function Calendar({ userId, userName }: { userId: string; userName: string }) {
  const { can } = useAuth();
  const { meta } = useLookups();
  const now = useNow();
  const canEdit = can('lead:write');
  const actions = useCalendarActions();

  // ------------------------------------------------------------------------------------------ where we are

  const [searchParams, setSearchParams] = useSearchParams();
  // Phones start on a single day; the week's seven columns do not fit.
  const [narrow] = useState(() => window.matchMedia?.('(max-width: 767px)').matches ?? false);
  const view = parseView(searchParams.get('view')) ?? (narrow ? 'day' : 'week');
  const date = parseDateParam(searchParams.get('date')) ?? startOfDay(now);
  const dateTime = date.getTime();
  const [agendaDays, setAgendaDays] = useState(AGENDA_DAYS);

  const [preferences, setPreferences] = useState(() => loadPreferences(userId));
  useEffect(() => savePreferences(userId, preferences), [userId, preferences]);
  const changePreferences = useCallback(
    (patch: Partial<CalendarPreferences>) => setPreferences((current) => ({ ...current, ...patch })),
    [],
  );
  const { weekStartsOn, showWeekends } = preferences;

  const range = useMemo(
    () => visibleRange(view, new Date(dateTime), { weekStartsOn, showWeekends, agendaDays }),
    [view, dateTime, weekStartsOn, showWeekends, agendaDays],
  );
  const title = periodTitle(view, date, range);

  // ------------------------------------------------------------------------------------------ popovers

  const [selected, setSelected] = useState<Selection | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftElement, setDraftElement] = useState<HTMLElement | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const go = useCallback(
    (nextView: CalendarView, nextDate: Date) => {
      setSelected(null);
      setDraft(null);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set('view', nextView);
        next.set('date', formatDateParam(nextDate));
        return next;
      });
    },
    [setSearchParams],
  );

  // ------------------------------------------------------------------------------------------ data

  const params = calendarQueryParams(range, preferences);
  const query = useQuery({
    queryKey: [...CALENDAR_RANGE_KEY, params],
    queryFn: ({ signal }) => api.get<CalendarResponse>(apiUrl('/api/calendar', params ?? {}), { signal }),
    enabled: params !== null,
    // Moving a week or ticking a checkbox keeps the old entries up until the new ones arrive.
    placeholderData: keepPreviousData,
  });
  const data = params ? query.data : undefined;

  const { hiddenTypes, showCompleted } = preferences;
  const entries = useMemo(() => {
    // Placeholder data may belong to another range or filter: show only what this one would return.
    const tasks = (data?.items ?? []).filter(
      (task) => !hiddenTypes.includes(task.type) && (showCompleted || task.status !== 'completed'),
    );
    return toCalendarEntries(tasks).filter(
      ({ timing }) => timing.start < range.end && (timing.end > range.start || timing.start >= range.start),
    );
  }, [data, hiddenTypes, showCompleted, range]);

  const personColors = useMemo(
    () => assignPersonColors((meta?.users ?? []).map((person) => person.id)),
    [meta],
  );
  const colorOf = useCallback(
    (task: TaskDTO): ColorToken =>
      preferences.colorBy === 'person'
        ? (personColors.get(task.assignedTo ?? '') ?? 'slate')
        : taskColor(task),
    [preferences.colorBy, personColors],
  );

  const people = useMemo<PersonOption[]>(() => {
    const users = meta?.users ?? [];
    const me = users.find((person) => person.id === userId);
    // Deactivated people stay listed while they are selected, so they can be unticked.
    const others = users
      .filter((person) => person.id !== userId && (person.isActive || preferences.people.includes(person.id)))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [
      { id: userId, label: `${me?.name ?? userName} (you)`, color: personColors.get(userId) ?? 'slate' },
      ...others.map((person) => ({
        id: person.id,
        label: person.name,
        color: personColors.get(person.id) ?? 'slate',
      })),
      { id: 'unassigned', label: 'Unassigned', color: 'slate' },
    ];
  }, [meta, userId, userName, preferences.people, personColors]);

  // Details follow the cache, so marking done or a reschedule shows at once.
  const selectedTask = selected
    ? (data?.items.find((task) => task.id === selected.task.id) ?? selected.task)
    : null;

  // ------------------------------------------------------------------------------------------ actions

  const openEntry = useCallback((task: TaskDTO, anchor: HTMLElement) => {
    setDraft(null);
    setSelected({ task, anchor });
  }, []);

  const startDraft = useCallback((slot: DraftSlot, anchor: HTMLElement | null = null) => {
    setSelected(null);
    setDraft({ slot, anchor, title: '' });
  }, []);

  /** Google's default: the next half hour today when today is on screen, else 9:00 on the date shown. */
  const openNew = () => {
    const today = startOfDay(now).getTime();
    const start = nextHalfHour(now);
    startDraft(
      range.days.some((day) => day.getTime() === today) ? slotAt(start, wallMinutes(start)) : daySlot(date),
    );
  };

  const saveDraft = async (entry: NewEntry) => {
    if (await actions.create(entry)) setDraft(null);
  };

  const moreOptions = ({ type, title: entryTitle, slot }: NewEntry) => {
    setDraft(null);
    setEditor({
      defaults: {
        type,
        title: entryTitle,
        dueAt: slot.start.toISOString(),
        endAt: slot.end.toISOString(),
        allDay: slot.allDay,
      },
    });
  };

  const editSelected = () => {
    if (!selectedTask) return;
    setSelected(null);
    setEditor({ task: selectedTask });
  };

  const deleteSelected = () => {
    if (!selectedTask) return;
    setSelected(null);
    void actions.remove(selectedTask);
  };

  const step = (direction: 1 | -1) => go(view, stepDate(view, date, direction, agendaDays));
  const today = () => go(view, now);

  useHotkeys(
    editor || draft
      ? {}
      : {
          t: today,
          d: () => go('day', date),
          w: () => go('week', date),
          m: () => go('month', date),
          a: () => go('agenda', date),
          j: () => step(1),
          n: () => step(1),
          k: () => step(-1),
          p: () => step(-1),
          ...(canEdit ? { c: openNew } : {}),
        },
  );

  // ------------------------------------------------------------------------------------------ render

  const sidebar = (
    <CalendarSidebar
      date={date}
      range={view === 'day' || view === 'week' ? range : null}
      now={now}
      people={people}
      preferences={preferences}
      onPreferencesChange={changePreferences}
      onSelectDate={(day) => {
        setSidebarOpen(false);
        go(view, day);
      }}
    />
  );

  const viewProps = {
    entries,
    now,
    canEdit,
    colorOf,
    selectedId: selected?.task.id ?? null,
    onOpenEntry: openEntry,
  };

  const body = (() => {
    if (query.isError && !data) {
      return (
        <EmptyState
          icon={AlertTriangle}
          title="The calendar could not be loaded"
          description="Check your connection and try again."
          action={<Button onClick={() => void query.refetch()}>Try again</Button>}
        />
      );
    }
    switch (view) {
      case 'day':
      case 'week':
        return (
          <TimeGridView
            {...viewProps}
            days={range.days}
            draft={draft?.slot ?? null}
            draftTitle={draft?.title ?? ''}
            draftRef={setDraftElement}
            onOpenDay={(day) => go('day', day)}
            onCreate={(slot) => startDraft(slot)}
            onReschedule={(task, change) => void actions.reschedule(task, change)}
          />
        );
      case 'month':
        return (
          <MonthView
            {...viewProps}
            days={range.days}
            month={date.getMonth()}
            onOpenDay={(day) => go('day', day)}
            onCreate={startDraft}
            onReschedule={(task, change) => void actions.reschedule(task, change)}
          />
        );
      case 'agenda':
        return params && query.isPending ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : (
          <AgendaView
            {...viewProps}
            range={range}
            onToggleDone={(task, done) => void actions.setDone(task, done)}
            onShowMore={
              agendaDays < MAX_AGENDA_DAYS
                ? () => setAgendaDays((days) => Math.min(days + AGENDA_DAYS, MAX_AGENDA_DAYS))
                : null
            }
            onNew={openNew}
          />
        );
    }
  })();

  return (
    <Page width="full" className="h-[calc(100dvh-3rem)] min-h-[36rem]">
      <PageHeader
        title="Calendar"
        actions={
          <>
            <div
              role="group"
              aria-label="View"
              className="flex items-center gap-0.5 rounded-md border border-slate-200 p-0.5"
            >
              {CALENDAR_VIEWS.map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={view === option ? 'primary' : 'ghost'}
                  aria-pressed={view === option}
                  title={`${VIEW_LABELS[option]} (${VIEW_HOTKEYS[option]})`}
                  onClick={() => go(option, date)}
                >
                  {VIEW_LABELS[option]}
                </Button>
              ))}
            </div>
            {canEdit && (
              <Button variant="primary" icon={Plus} onClick={openNew} title="New entry (C)">
                New
              </Button>
            )}
          </>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <IconButton
            icon={PanelLeft}
            label="People and filters"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          />
          <Button size="sm" onClick={today} title="Today (T)">
            Today
          </Button>
          <div className="flex items-center">
            <IconButton
              icon={ChevronLeft}
              label={`Previous ${STEP_NOUNS[view]}`}
              size="sm"
              onClick={() => step(-1)}
            />
            <IconButton
              icon={ChevronRight}
              label={`Next ${STEP_NOUNS[view]}`}
              size="sm"
              onClick={() => step(1)}
            />
          </div>
          <h2 aria-live="polite" className="min-w-0 truncate text-base font-semibold text-slate-900">
            {title}
          </h2>
          {query.isFetching && <Spinner size={14} label="Loading entries" className="text-slate-400" />}
        </div>

        {params === null && (
          <p
            role="status"
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900"
          >
            Tick at least one person and one entry type to see entries.
          </p>
        )}
        {data?.truncated && (
          <p
            role="status"
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900"
          >
            Showing the first {formatNumber(CALENDAR_MAX_ITEMS)} entries. Untick some people or entry types to
            see the rest.
          </p>
        )}

        <div className="flex min-h-0 flex-1 gap-4">
          <aside
            aria-label="People and filters"
            className="hidden w-60 shrink-0 overflow-y-auto pr-1 lg:block"
          >
            {sidebar}
          </aside>
          <section
            aria-label={title}
            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-surface shadow-xs"
          >
            {body}
          </section>
        </div>
      </div>

      <SidePanel
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        title="People and filters"
        width="md"
      >
        {sidebar}
      </SidePanel>

      {selectedTask && selected && (
        <EntryDetailsPopover
          task={selectedTask}
          anchor={selected.anchor}
          color={colorOf(selectedTask)}
          now={now}
          canEdit={canEdit}
          busy={actions.busy}
          onClose={() => setSelected(null)}
          onEdit={editSelected}
          onDelete={deleteSelected}
          onToggleDone={(done) => void actions.setDone(selectedTask, done)}
        />
      )}

      {draft && (
        <QuickCreatePopover
          slot={draft.slot}
          anchor={draft.anchor ?? draftElement}
          now={now}
          busy={actions.busy}
          title={draft.title}
          onTitleChange={(title) => setDraft({ ...draft, title })}
          onSlotChange={(slot) => setDraft({ ...draft, slot })}
          onSave={(entry) => void saveDraft(entry)}
          onMoreOptions={moreOptions}
          onClose={() => setDraft(null)}
        />
      )}

      {editor && <TaskFormPanel {...editor} onClose={() => setEditor(null)} />}

      {actions.element}
    </Page>
  );
}
